import { Role } from 'auth';
import classNames from 'classnames';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import IssuerDocumentsFilters from '@/modules/issuer-dashboard/components/IssuerDocumentsFilters.tsx';
import { IssuerTransactionsOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { IssuerTransferOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransfersOperationInfoModal.tsx';
import { useGetDocumentsQuery } from '@/modules/issuer-dashboard/queries/documents.ts';
import {
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DownloadGreyIcon } from 'ui/icons';
import { GetDocumentsV1SrDocumentsGetParams } from 'api';

export type IssuerDocumentFilters = Pick<
  GetDocumentsV1SrDocumentsGetParams,
  'shareholder_id' | 'operation_type' | 'operation_id' | 'date_from' | 'date_to'
>;

const headItems: TableHeadColumn[] = [
  {
    title: 'Document name',
    className: '!py-2 text-xs !leading-[20px] !text-gray-500 min-w-[120px]',
  },
  { title: 'Operation', className: '!py-2 text-xs !leading-[20px] !text-gray-500 min-w-[200px]' },
  { title: 'Uploaded by', className: '!py-2 text-xs !leading-[20px] !text-gray-500 min-w-[125px]' },
  { title: 'Date', className: '!py-2 text-xs !leading-[20px] !text-gray-500 min-w-[170px]' },
  { title: '', className: '!py-2 text-xs !leading-[20px] !text-gray-500 block w-[50px]' },
];

const IssuerDocuments = () => {
  const { isMobile } = useIsMobile();
  const [filters, setFilters] = useState<IssuerDocumentFilters>({});

  const { data, isLoading, hasNextPage, fetchNextPage } = useGetDocumentsQuery({
    page: 1,
    page_size: 20,
    ...filters,
  });

  const documents = useMemo<TableBodyColumn[]>(() => {
    const documents = (data?.pages || []).flatMap(({ data }) => data);
    return documents.map(
      ({
        name,
        id,
        belong_item_type,
        file_storage_item_url,
        operation_document_date,
        user,
        operation_type,
      }) => ({
        cells: [
          { cell: name },
          {
            cell: (
              <ModalOperationsTransactions
                transactionId={id}
                belong_item_type={belong_item_type}
                operation_type={operation_type}
              />
            ),
          },
          { cell: user ? `${user.first_name} ${user.last_name}` : '-' },
          {
            cell: operation_document_date
              ? dayjs(operation_document_date).format('DD-MM-YYYY HH:mm')
              : '-',
          },
          {
            cell: (
              <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center sm:justify-end justify-center">
                <a href={file_storage_item_url} className="self-center" target="_blank">
                  <img src={DownloadGreyIcon} alt="download" className="hover:opacity-70" />
                </a>
              </div>
            ),
          },
        ],
      }),
    );
  }, [data?.pages]);

  return (
    <PageLayout
      title="Documents"
      headerClassname="!pb-2 !px-0 [&>div:first-child]:px-3 [&>div:first-child]:sm:px-6"
      contentClassName="px-0 py-0"
      headerBottomContent={<IssuerDocumentsFilters filters={filters} setFilters={setFilters} />}
    >
      {!isLoading && !!documents.length && (
        <Table
          classNameHead="border-t-none"
          blockIdInfinite="scrollable"
          headItems={headItems}
          items={documents}
          hasNextPageInfinite={hasNextPage}
          withRounded={isMobile}
          withBorderX={false}
          fetchNextPageInfinite={fetchNextPage}
          className="my-0 border-none"
        />
      )}
      {isLoading && (
        <div className="h-full w-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      <EmptyListState
        title="No documents"
        description="Documents attached to transactions and transfers will appear here."
        isLoading={isLoading}
        isSearchUsed={!isEmpty(filters)}
        itemsCount={documents.length}
      />
    </PageLayout>
  );
};

interface ModalOperationsTransactionsProps {
  belong_item_type: string;
  transactionId: number;
  operation_type?: string;
}

export const ModalOperationsTransactions = ({
  transactionId,
  operation_type,
  belong_item_type,
}: ModalOperationsTransactionsProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const handleOpenOperationInfoModal = useCallback(
    (id: number, belong_item_type: 'transfer' | 'transaction') => {
      if (belong_item_type === 'transaction') {
        openModal(IssuerTransactionsOperationInfoModal, {
          id,
          registerId,
          registerCountry,
        });
      }
      if (belong_item_type === 'transfer') {
        openModal(IssuerTransferOperationInfoModal, {
          id,
        });
      }
    },
    [openModal, registerCountry, registerId],
  );

  return (
    <span
      className={classNames(
        'flex gap-4 items-center',
        'text-[#0369A1] hover:underline cursor-pointer',
      )}
      onClick={() =>
        handleOpenOperationInfoModal(transactionId, belong_item_type as 'transfer' | 'transaction')
      }
    >
      <span>
        {capitalize(lowerCase(operation_type))}
        {' #'}
        <span>{transactionId}</span>
      </span>
    </span>
  );
};

export default withPrivateAccess(IssuerDocuments, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
